/**
 * Feature Management Page
 * Phase 6.5: Full feature control with search, categories, bulk actions, and dependencies
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Wallet,
  Bot,
  Coins,
  Shield,
  Settings,
  Zap,
} from 'lucide-react';

import { getHotelFeatures, toggleHotelFeature } from '@/lib/blockchainApi';

// Feature categories with icons and colors
const CATEGORIES = {
  ai_agents: {
    label: 'AI Agents',
    icon: Bot,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50 dark:bg-purple-950/20',
    description: 'Intelligent automation for guest experience and operations'
  },
  token_economy: {
    label: 'Token Economy',
    icon: Coins,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50 dark:bg-amber-950/20',
    description: 'HERMES token staking, rewards, and guest loyalty'
  },
  web3: {
    label: 'Web3 Features',
    icon: Wallet,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50 dark:bg-cyan-950/20',
    description: 'Blockchain integrations for payments and identity'
  },
  core: {
    label: 'Core Systems',
    icon: Shield,
    color: 'text-green-600',
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    description: 'Essential blockchain infrastructure and monitoring'
  }
};

// Feature metadata with categories and dependencies
const FEATURE_METADATA = {
  bitsy_agent: {
    category: 'ai_agents',
    name: 'Bitsy (Guest Concierge)',
    description: 'AI-powered guest sentiment analysis and personalization',
    dependencies: [],
    requiresWallet: false
  },
  marina_agent: {
    category: 'ai_agents',
    name: 'Marina (Operations Manager)',
    description: 'AI task routing, staff coordination, and optimization',
    dependencies: [],
    requiresWallet: false
  },
  sage_agent: {
    category: 'ai_agents',
    name: 'Sage (Strategy & Finance)',
    description: 'Performance reporting, revenue optimization, governance',
    dependencies: [],
    requiresWallet: false
  },
  hermes_token: {
    category: 'token_economy',
    name: 'HERMES Token System',
    description: 'Core token economy infrastructure',
    dependencies: ['bitsy_agent', 'marina_agent', 'sage_agent'],
    requiresWallet: true
  },
  guest_loyalty: {
    category: 'token_economy',
    name: 'Guest Loyalty Rewards',
    description: 'Token rewards for trusted guests',
    dependencies: ['hermes_token'],
    requiresWallet: false
  },
  hotel_staking: {
    category: 'token_economy',
    name: 'Hotel Staking',
    description: 'Stake HERMES tokens for network participation',
    dependencies: ['hermes_token'],
    requiresWallet: true
  },
  quality_scoring: {
    category: 'core',
    name: 'Quality Scoring System',
    description: 'Automated quality tier calculation',
    dependencies: [],
    requiresWallet: false
  },
  acp_escrow: {
    category: 'core',
    name: 'Agent Commerce Protocol',
    description: 'Escrow for agent-to-agent transactions',
    dependencies: ['bitsy_agent', 'marina_agent', 'sage_agent'],
    requiresWallet: true
  },
  event_indexing: {
    category: 'core',
    name: 'Event Indexing',
    description: 'Index blockchain events for real-time monitoring',
    dependencies: [],
    requiresWallet: false
  },
  agent_telemetry: {
    category: 'core',
    name: 'Agent Telemetry',
    description: 'Monitor AI agent performance and activity',
    dependencies: [],
    requiresWallet: false
  },
  nft_receipts: {
    category: 'web3',
    name: 'NFT Booking Receipts',
    description: 'Mint NFT receipts for bookings on blockchain',
    dependencies: [],
    requiresWallet: true
  },
  sbt_identity: {
    category: 'web3',
    name: 'Soulbound Identity Tokens',
    description: 'Non-transferable identity verification tokens',
    dependencies: [],
    requiresWallet: true
  },
  crypto_payments: {
    category: 'web3',
    name: 'Crypto Payment Processing',
    description: 'Accept cryptocurrency payments (ETH, USDC, etc.)',
    dependencies: [],
    requiresWallet: true
  },
  splits_router: {
    category: 'web3',
    name: '0xSplits Auto-Router',
    description: 'Automatic payment splitting to hotel and platform wallets',
    dependencies: ['crypto_payments'],
    requiresWallet: true
  }
};

export default function FeatureManagementPage() {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  
  const [features, setFeatures] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    ai_agents: true,
    token_economy: true,
    web3: true,
    core: true
  });
  const [walletConfigured, setWalletConfigured] = useState(false);

  // Load features on mount
  useEffect(() => {
    if (hotelId) {
      loadFeatures();
    }
  }, [hotelId]);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const response = await getHotelFeatures(hotelId);
      setFeatures(response.features || {});
      setWalletConfigured(response.wallet_configured || false);
    } catch (error) {
      console.error('Failed to load features:', error);
      toast.error('Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  // Group features by category with search filtering
  const groupedFeatures = useMemo(() => {
    if (!features) return {};

    const groups = {};
    Object.entries(FEATURE_METADATA).forEach(([featureId, metadata]) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          metadata.name.toLowerCase().includes(query) ||
          metadata.description.toLowerCase().includes(query) ||
          featureId.toLowerCase().includes(query);
        
        if (!matchesSearch) return;
      }

      const category = metadata.category;
      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push({
        id: featureId,
        ...metadata,
        enabled: features[featureId]?.enabled || false,
        status: getFeatureStatus(featureId, features[featureId])
      });
    });

    return groups;
  }, [features, searchQuery]);

  // Get feature status (enabled, disabled, blocked)
  const getFeatureStatus = (featureId, featureData) => {
    if (!featureData) return 'disabled';
    
    const metadata = FEATURE_METADATA[featureId];
    
    // Check wallet requirement
    if (metadata.requiresWallet && !walletConfigured) {
      return 'blocked';
    }
    
    // Check dependencies
    if (metadata.dependencies?.length > 0) {
      const unmetDeps = metadata.dependencies.filter(depId => !features[depId]?.enabled);
      if (unmetDeps.length > 0) {
        return 'blocked';
      }
    }
    
    return featureData.enabled ? 'enabled' : 'disabled';
  };

  // Toggle single feature
  const handleToggleFeature = async (featureId) => {
    try {
      const result = await toggleHotelFeature(hotelId, featureId);
      
      setFeatures(prev => ({
        ...prev,
        [featureId]: {
          ...prev[featureId],
          enabled: result.enabled
        }
      }));

      toast.success(`Feature ${result.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle feature:', error);
      toast.error(error.message || 'Failed to toggle feature');
    }
  };

  // Bulk enable category
  const handleBulkEnable = async (category) => {
    const categoryFeatures = groupedFeatures[category] || [];
    const toEnable = categoryFeatures.filter(f => !f.enabled && f.status !== 'blocked');
    
    if (toEnable.length === 0) {
      toast.info('All features in this category are already enabled or blocked');
      return;
    }

    try {
      await Promise.all(
        toEnable.map(f => toggleHotelFeature(hotelId, f.id))
      );
      
      await loadFeatures();
      toast.success(`Enabled ${toEnable.length} features in ${CATEGORIES[category].label}`);
    } catch (error) {
      console.error('Bulk enable failed:', error);
      toast.error('Failed to enable all features');
    }
  };

  // Bulk disable category
  const handleBulkDisable = async (category) => {
    const categoryFeatures = groupedFeatures[category] || [];
    const toDisable = categoryFeatures.filter(f => f.enabled);
    
    if (toDisable.length === 0) {
      toast.info('All features in this category are already disabled');
      return;
    }

    try {
      await Promise.all(
        toDisable.map(f => toggleHotelFeature(hotelId, f.id))
      );
      
      await loadFeatures();
      toast.success(`Disabled ${toDisable.length} features in ${CATEGORIES[category].label}`);
    } catch (error) {
      console.error('Bulk disable failed:', error);
      toast.error('Failed to disable all features');
    }
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'enabled':
        return (
          <Badge variant="default" className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Enabled
          </Badge>
        );
      case 'disabled':
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <XCircle className="h-3 w-3" />
            Disabled
          </Badge>
        );
      case 'blocked':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Blocked
          </Badge>
        );
      default:
        return null;
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Render feature card
  const renderFeatureCard = (feature) => {
    const isBlocked = feature.status === 'blocked';
    const blockReason = feature.requiresWallet && !walletConfigured 
      ? 'Configure wallet in Finance & Billing'
      : feature.dependencies?.length > 0
      ? `Requires: ${feature.dependencies.map(d => FEATURE_METADATA[d]?.name).join(', ')}`
      : null;

    return (
      <div
        key={feature.id}
        className={`p-4 border rounded-lg transition-colors ${
          feature.enabled ? 'bg-muted/30' : ''
        }`}
        data-testid={`feature-card-${feature.id}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold">{feature.name}</h4>
              {renderStatusBadge(feature.status)}
            </div>
            <p className="text-sm text-muted-foreground">{feature.description}</p>
            
            {isBlocked && blockReason && (
              <Alert variant="destructive" className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  {blockReason}
                </AlertDescription>
              </Alert>
            )}

            {feature.dependencies?.length > 0 && feature.status !== 'blocked' && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Zap className="h-3 w-3" />
                Dependencies: {feature.dependencies.map(d => FEATURE_METADATA[d]?.name).join(', ')}
              </div>
            )}
          </div>

          <Switch
            checked={feature.enabled}
            onCheckedChange={() => handleToggleFeature(feature.id)}
            disabled={isBlocked}
            data-testid={`feature-toggle-${feature.id}`}
            aria-label={`Toggle ${feature.name}`}
          />
        </div>
      </div>
    );
  };

  // Render category section
  const renderCategory = (categoryId) => {
    const category = CATEGORIES[categoryId];
    const categoryFeatures = groupedFeatures[categoryId] || [];
    
    if (categoryFeatures.length === 0) return null;

    const Icon = category.icon;
    const isExpanded = expandedCategories[categoryId];
    const enabledCount = categoryFeatures.filter(f => f.enabled).length;
    const totalCount = categoryFeatures.length;

    return (
      <Card key={categoryId} className={category.bgColor}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-3 cursor-pointer flex-1"
              onClick={() => toggleCategory(categoryId)}
            >
              <Icon className={`h-6 w-6 ${category.color}`} />
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2">
                  {category.label}
                  <Badge variant="outline" className="ml-2">
                    {enabledCount}/{totalCount}
                  </Badge>
                </CardTitle>
                <CardDescription className="mt-1">
                  {category.description}
                </CardDescription>
              </div>
              {isExpanded ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </div>

          {isExpanded && (
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkEnable(categoryId)}
                data-testid={`bulk-enable-${categoryId}`}
              >
                Enable All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkDisable(categoryId)}
                data-testid={`bulk-disable-${categoryId}`}
              >
                Disable All
              </Button>
            </div>
          )}
        </CardHeader>

        {isExpanded && (
          <CardContent>
            <div className="space-y-3">
              {categoryFeatures.map(renderFeatureCard)}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6 p-6" data-testid="feature-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl heading-font tracking-tight">
            Feature Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Control all blockchain features for your hotel
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadFeatures}
          data-testid="refresh-features-button"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Wallet Warning */}
      {!walletConfigured && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Web3 features are blocked.</strong> Configure your wallet in Finance & Billing to enable NFT receipts, identity tokens, and crypto payments.
          </AlertDescription>
        </Alert>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search features..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="feature-search-input"
        />
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {Object.keys(CATEGORIES).map(renderCategory)}
      </div>

      {/* Empty state */}
      {Object.keys(groupedFeatures).length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              No features match your search query
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
